import React, { useState, useEffect } from "react";
import { Button, Form, Modal, Spinner, Alert } from "react-bootstrap";
import { FiRefreshCw } from "react-icons/fi";
import { useReportTemplateActions } from "../hooks/useReportTemplateActions";
import { ReportTemplate, OriginScope } from "../types/reportTemplate";
import { showSuccess } from "@infrastructure/api/utils/toastNotifications";
import { ErrorResponse } from "@core/types/errorResponse";
import { useApiError } from "@infrastructure/api/hooks/useApiError";
import { useErrorModalStore } from "@infrastructure/api/store/errorModalStore";
import { useSearchParams } from "react-router-dom";
import { useScopeId } from "@core/hooks/useRouteParamId";
import { usePermissions } from "@features/entity/hooks/usePermissions";
import { ACTION_CODES, TEMPLATE_ORIGIN_CREATE_CODES, getTemplateOriginUpdateCode } from "@features/entity/types/actionCodes";

const ORIGIN_SCOPE_OPTIONS: { value: OriginScope; label: string }[] = [
    { value: 'CHURCH', label: 'Church' },
    { value: 'DISTRICT', label: 'District' },
    { value: 'NATION', label: 'Nation' },
];

interface Props {
    show: boolean;
    onHide: () => void;
    template: ReportTemplate | null;
    onChanged?: () => void;
}

const ChangeOriginScopeModal: React.FC<Props> = ({ show, onHide, template, onChanged }) => {
    const [searchParams] = useSearchParams();
    const resolvedDepartmentId = Number(searchParams.get("did"));
    const resolvedNationId = useScopeId() ?? 0;

    const [selectedScope, setSelectedScope] = useState<OriginScope | ''>('');
    const showError = useApiError();
    const showValidation = useErrorModalStore((s) => s.showValidation);
    const { busy, doTemplateChangeOriginScope } = useReportTemplateActions();

    const fromOriginCode = template?.origin_scope
        ? getTemplateOriginUpdateCode(template.origin_scope)
        : '';

    const { hasPermission } = usePermissions({
        scopeType: "NATION",
        scopeId: resolvedNationId || undefined,
        actionCodes: [...TEMPLATE_ORIGIN_CREATE_CODES, fromOriginCode].filter(Boolean),
    });

    const canCreateChurch = hasPermission(ACTION_CODES.CREATE_CHURCH_REPORT_TEMPLATE);
    const canCreateDistrict = hasPermission(ACTION_CODES.CREATE_DISTRICT_REPORT_TEMPLATE);
    const canCreateNation = hasPermission(ACTION_CODES.CREATE_NATION_REPORT_TEMPLATE);

    const availableScopes = ORIGIN_SCOPE_OPTIONS.filter((opt) => {
        if (opt.value === 'CHURCH') return canCreateChurch;
        if (opt.value === 'DISTRICT') return canCreateDistrict;
        if (opt.value === 'NATION') return canCreateNation;
        return false;
    });

    useEffect(() => {
        if (template?.origin_scope) {
            setSelectedScope(template.origin_scope);
        } else {
            setSelectedScope('');
        }
    }, [template, show]);

    const handleClose = () => {
        setSelectedScope('');
        onHide();
    };

    const handleSubmit = async () => {
        const messages: string[] = [];
        if (!template) messages.push("No template selected.");
        if (!selectedScope) messages.push("Please select who will complete this template.");
        if (selectedScope === template?.origin_scope) messages.push("No change detected. Please select a different scope.");
        if (messages.length > 0) {
            showValidation(messages);
            return;
        }

        try {
            const response = await doTemplateChangeOriginScope(
                resolvedNationId,
                resolvedDepartmentId,
                template!.report_template_id,
                selectedScope as OriginScope
            );

            if (response.isSuccess()) {
                showSuccess(response.getData()?.message ?? "Origin scope updated successfully.");
                handleClose();
                onChanged?.();
            } else {
                const error = response.getError() as ErrorResponse;
                showError(error);
            }
        } catch (error) {
            showError(error as ErrorResponse);
        }
    };

    const currentScopeLabel = template?.origin_scope
        ? ORIGIN_SCOPE_OPTIONS.find((o) => o.value === template.origin_scope)?.label || template.origin_scope
        : 'Not set';

    return (
        <Modal
            show={show}
            onHide={handleClose}
            backdrop="static"
            keyboard={false}
            centered
            size="lg"
            data-testid="change-origin-scope-modal"
        >
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="fw-semibold">
                    <FiRefreshCw className="me-2" aria-hidden />
                    Change Who Completes This
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-3 pb-4">
                <Alert variant="info" className="mb-3">
                    <strong>Current setting:</strong> {currentScopeLabel}
                </Alert>

                <p className="text-muted mb-3">
                    Select who will be responsible for completing this report template.
                    This determines where the template appears in the submission workflow.
                </p>

                {template && (
                    <div className="mb-3 p-3 bg-light rounded">
                        <div className="fw-semibold">{template.report_template_name}</div>
                        {template.description && (
                            <div className="text-muted small mt-1">{template.description}</div>
                        )}
                    </div>
                )}

                <Form>
                    <Form.Group className="mb-3" controlId="originScope">
                        <Form.Label className="fw-semibold">
                            To be completed by <span className="text-danger">*</span>
                        </Form.Label>
                        {availableScopes.length === 0 ? (
                            <Alert variant="warning" className="mb-0">
                                No origin scopes are available based on current permissions.
                            </Alert>
                        ) : (
                            <Form.Select
                                value={selectedScope}
                                onChange={(e) => setSelectedScope(e.target.value as OriginScope | '')}
                                disabled={busy}
                            >
                                <option value="">Select who will complete this…</option>
                                {availableScopes.map((opt) => (
                                    <option key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </option>
                                ))}
                            </Form.Select>
                        )}
                    </Form.Group>

                    <div className="d-flex justify-content-end gap-2 mt-4">
                        <Button
                            variant="outline-secondary"
                            onClick={handleClose}
                            disabled={busy}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            disabled={busy || !selectedScope || selectedScope === template?.origin_scope}
                            aria-busy={busy}
                        >
                            {busy ? (
                                <>
                                    <Spinner animation="border" size="sm" className="me-2" />
                                    Updating…
                                </>
                            ) : (
                                "Save Change"
                            )}
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default ChangeOriginScopeModal;
